import React, { useEffect, useState } from "react";
import "./LoginPage.css"; // Import the CSS file
import { useNavigate } from "react-router-dom";
import { useLogin } from "../../context/LoginProvider";
import { login, signInWithGoogle } from "../../utils/auth";
import AlertBox from "../../components/AlertBox/AlertBox";
import { useGoogleLogin } from "@react-oauth/google";
import { useSocket } from "../../context/SocketProvider";
import Navbar from "../../components/Navbar";

function LoginPage() {
  const { userData, setUserData, isLoggedIn, setLoggedIn, setGuestUserData } =
    useLogin();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isdisabled, setDisabled] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");

  const { socket, waitForConnection } = useSocket();
  const navigate = useNavigate();

  useEffect(() => {
    if (isLoggedIn) navigate("/", { replace: true });
  }, [isLoggedIn]);

  const acquireSession = async (userData) => {
    console.log("Acquiring session for: ", userData);
    return new Promise((resolve, reject) => {
      try {
        // First, authenticate with the server
        console.log(
          "Sending authentication request with token:",
          userData.token ? "Token exists" : "No token"
        );
        socket.emit("authenticate", { token: userData.token }, (response) => {
          console.log("Authentication response:", response);
          if (!response) {
            console.error("Authentication failed: No response from server");
            reject(new Error("Authentication failed: No response from server"));
            return;
          }

          if (response.error) {
            console.error("Authentication failed:", response.error);
            reject(new Error(response.error));
            return;
          }

          console.log("Authentication successful, user:", response.user);

          // After authentication, request embeddings for the user's interests
          if (userData.interests && userData.interests.length > 0) {
            console.log(
              "Requesting embeddings for interests:",
              userData.interests
            );
            // Request embeddings for the user's interests
            socket.emit("getEmbeddings", { interests: userData.interests });

            // Set up a one-time listener for the embeddings response
            const handleEmbeddings = (data) => {
              console.log(
                "Received embeddings:",
                data.embeddings ? "Embeddings exist" : "No embeddings"
              );
              // Remove the listener after receiving the response
              socket.off("postEmbeddings", handleEmbeddings);

              // Join with the received embeddings
              joinWithEmbeddings(userData, data.embeddings, resolve, response);
            };

            // Listen for the embeddings response
            socket.on("postEmbeddings", handleEmbeddings);

            // Set a timeout in case the embeddings response never comes
            setTimeout(() => {
              console.log("Embeddings timeout, using default embeddings");
              socket.off("postEmbeddings", handleEmbeddings);
              // Join with default embeddings if no response
              joinWithEmbeddings(
                userData,
                new Array(1536).fill(0).map(() => Math.random() * 0.01),
                resolve,
                response
              );
            }, 5000);
          } else {
            console.log("No interests, using default embeddings");
            // If no interests, join with default embeddings
            joinWithEmbeddings(userData, [0.1, 0.2, 0.3], resolve, response);
          }
        });
      } catch (error) {
        console.error("Session acquisition error:", error);
        reject(error);
      }
    });
  };

  // Helper function to join with embeddings
  const joinWithEmbeddings = (userData, embeddings, resolve, response) => {
    console.log("Joining with embeddings, username:", userData.username);
    socket.emit(
      "join",
      {
        embeddings: embeddings,
        username: userData.username,
        isLoggedIn: true,
      },
      (joinResponse) => {
        console.log("Join response:", joinResponse);
        if (joinResponse && joinResponse.error) {
          console.warn("Failed to join room:", joinResponse.error);
          // Don't reject here, just log the warning
        } else if (joinResponse) {
          console.log("Successfully joined room");
        }
        resolve(response.user);
      }
    );
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setDisabled(true);
    setError("");
    setAlertMessage("");

    // Input validation
    if (!email.trim() || !password.trim()) {
      setAlertMessage("Please fill in all fields");
      setShowAlert(true);
      setDisabled(false);
      return;
    }

    try {
      const response = await fetch("https://soulmegle.com/api/auth/login", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email, password }),
        credentials: "include",
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Login failed");
      }

      setUserData({
        name: data.name,
        username: data.username,
        email: data.email || email,
        age: data.age,
        country: data.country,
        relationshipType: data.relationshipType,
        interests: data.interests || [],
        profilePhoto: data.profilePhoto || "",
        theme: data.theme || "purple",
        joinDate: data.joinDate || "",
        chatHistory: data.chatHistory || [],
      });
      setLoggedIn(true);

      if (data.interests && data.interests.length > 0) {
        navigate("/profile-summary");
      } else {
        navigate("/cupid");
      }
    } catch (error) {
      setAlertMessage(error.message || "Invalid email or password");
      setShowAlert(true);
    } finally {
      setDisabled(false);
    }
  };

  async function handleGoogleLoginSuccess(tokenResponse) {
    try {
      const data = await signInWithGoogle(tokenResponse);
      console.log("Sign-In With Google Result:", data);

      // Set user data and logged in state first
      setLoggedIn(true);
      setUserData({
        name: data.name,
        username: data.username,
        email: data.email,
        age: data.age,
        country: data.country,
        relationshipType: data.relationshipType,
        interests: data.interests || [],
        profilePhoto: data.profilePhoto || "",
        theme: data.theme || "purple",
        joinDate: data.joinDate || "",
        chatHistory: data.chatHistory || [],
        token: data.token,
      });

      // Then acquire session using the socket
      try {
        const sessionUser = await acquireSession(data);
        console.log("Session acquired successfully for Google login");
      } catch (sessionError) {
        console.error(
          "Failed to acquire session for Google login:",
          sessionError
        );
        // Continue with navigation even if session acquisition fails
      }

      if (data.interests.length === 0) navigate("/cupid", { replace: true });
      else navigate("/chat", { replace: true });
    } catch (error) {
      console.error("Google Sign-In Error:", error);
      setAlertMessage(
        error.message || "An error occurred during Google Sign-In"
      );
      setShowAlert(true);
    }
  }
  const loginWithGoogle = useGoogleLogin({
    onSuccess: handleGoogleLoginSuccess,
    onError: (error) => {
      console.error("Google Sign-In Error:", error);
      setAlertMessage("Google Sign-In failed. Please try again.");
      setShowAlert(true);
    },
    flow: "implicit",
    ux_mode: "popup",
    use_fedcm_for_prompt: true,
  });
  const closeAlert = () => {
    setShowAlert(false);
  };

  const handleGuestLogin = () => {
    // Create a fresh guest user and clear any cached data
    const RandomNumber = Math.floor(100000 + Math.random() * 900000).toString();
    const newUsername = "guest" + Date.now() + RandomNumber;
    const freshGuestData = { 
      username: newUsername, 
      name: "",
      age: "",
      country: "",
      interests: [],
      isFreshGuest: true // Flag to indicate this is a fresh guest user
    };
    
    // Clear any existing guest data from localStorage
    localStorage.removeItem("guestUser");
    
    // Set the fresh guest data
    setGuestUserData(freshGuestData);
    
    // Store the fresh guest data in localStorage
    localStorage.setItem("guestUser", JSON.stringify(freshGuestData));
    
    console.log("[LoginPage] Created fresh guest user:", newUsername);
    
    // Navigate to guest info form to collect guest information
    navigate("/guest-info", { replace: true });
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleLogin(e);
    }
  };

  return (
    <>
      <Navbar isHomePage={false} />
      <div className="login-page">
        <div className="login-form">
          <div className="login-container">
          <div className="login-header">
              <div className="brand-accent"></div>
              <h1>Welcome Back</h1>
              <p>Sign in to continue your journey</p>
          </div>
            
            <form onSubmit={handleLogin} className="login-form-content">
            <div className="form-group">
              <label className="form-label" htmlFor="login-email">
                  <svg className="label-icon" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z"/>
                    <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z"/>
                  </svg>
                  Email Address
            </label>
            <div className="input-container">
              <input
                id="login-email"
                type="email"
                name="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Enter your email"
                required
              />
            </div>
            </div>

            <div className="form-group">
              <label className="form-label" htmlFor="login-password">
                  <svg className="label-icon" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd"/>
                  </svg>
              Password
            </label>
            <div className="input-container">
              <input
                id="login-password"
                type={showPassword ? "text" : "password"}
                name="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Enter your password"
                required
              />
              <button
                type="button"
                className="toggle-password"
                  onClick={() => setShowPassword((prev) => !prev)}
                >
                    <svg fill="currentColor" viewBox="0 0 20 20">
                      {showPassword ? (
                        <path fillRule="evenodd" d="M3.707 2.293a1 1 0 00-1.414 1.414l14 14a1 1 0 001.414-1.414l-1.473-1.473A10.014 10.014 0 0019.542 10C18.268 5.943 14.478 3 10 3a9.958 9.958 0 00-4.512 1.074l-1.78-1.781zm4.261 4.26l1.514 1.515a2.003 2.003 0 012.45 2.45l1.514 1.514a4 4 0 00-5.478-5.478z" clipRule="evenodd"/>
                      ) : (
                        <path d="M10 12a2 2 0 100-4 2 2 0 000 4z"/>
                      )}
                      <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd"/>
                    </svg>
              </button>
              </div>
            </div>

              <div className="forgot-password">
              <button
                type="button"
                onClick={() => navigate("/forgot-password", { replace: true })}
              >
                  Forgot Password?
              </button>
            </div>

            <div className="submit-container">
                <button type="submit" disabled={isdisabled} className="login-btn">
                  {isdisabled ? (
                    <>
                      <div className="loading-spinner"></div>
                      Signing In...
                    </>
                  ) : (
                    <>
                      <svg className="btn-icon" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M3 3a1 1 0 000 2v8a2 2 0 002 2h2.586l-1.293 1.293a1 1 0 101.414 1.414L10 15.414l2.293 2.293a1 1 0 001.414-1.414L12.414 15H15a2 2 0 002-2V5a1 1 0 100-2H3zm11.707 4.707a1 1 0 00-1.414-1.414L10 9.586 8.707 8.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
                      </svg>
                      Sign In
                    </>
                  )}
              </button>
            </div>

            <div className="signup-link">
                <span>Don't have an account?</span>
              <button type="button" onClick={() => navigate("/signup", { replace: true })}>
                  Create Account
              </button>
            </div>
          </form>

            <div className="divider">
              <div className="divider-line"></div>
              <span className="divider-text">Or continue with</span>
              <div className="divider-line"></div>
          </div>

            <div className="social-buttons">
              <button className="google-button" onClick={loginWithGoogle}>
            <svg
                  width="20"
                  height="20"
              viewBox="0 0 25 25"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M22.3055 10.5415H21.5V10.5H12.5V14.5H18.1515C17.327 16.8285 15.1115 18.5 12.5 18.5C9.1865 18.5 6.5 15.8135 6.5 12.5C6.5 9.1865 9.1865 6.5 12.5 6.5C14.0295 6.5 15.421 7.077 16.4805 8.0195L19.309 5.191C17.523 3.5265 15.134 2.5 12.5 2.5C6.9775 2.5 2.5 6.9775 2.5 12.5C2.5 18.0225 6.9775 22.5 12.5 22.5C18.0225 22.5 22.5 18.0225 22.5 12.5C22.5 11.8295 22.431 11.175 22.3055 10.5415Z"
                fill="#FFC107"
              />
              <path
                d="M3.65308 7.8455L6.93858 10.255C7.82758 8.054 9.98058 6.5 12.5001 6.5C14.0296 6.5 15.4211 7.077 16.4806 8.0195L19.3091 5.191C17.5231 3.5265 15.1341 2.5 12.5001 2.5C8.65908 2.5 5.32808 4.6685 3.65308 7.8455Z"
                fill="#FF3D00"
              />
              <path
                d="M12.4999 22.4999C15.0829 22.4999 17.4299 21.5114 19.2044 19.9039L16.1094 17.2849C15.0717 18.074 13.8036 18.5009 12.4999 18.4999C9.89891 18.4999 7.69041 16.8414 6.85841 14.5269L3.59741 17.0394C5.25241 20.2779 8.61341 22.4999 12.4999 22.4999Z"
                fill="#4CAF50"
              />
              <path
                d="M22.3055 10.5415H21.5V10.5H12.5V14.5H18.1515C17.7571 15.6082 17.0467 16.5766 16.108 17.2855L16.1095 17.2845L19.2045 19.9035C18.9855 20.1025 22.5 17.5 22.5 12.5C22.5 11.8295 22.431 11.175 22.3055 10.5415Z"
                fill="#1976D2"
              />
            </svg>
                Google
          </button>
              
          <button className="guest-button" onClick={handleGuestLogin}>
            <svg
                  width="20"
                  height="20"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"
                fill="currentColor"
              />
            </svg>
                Guest
          </button>
            </div>
          </div>
        </div>
        
        <div className="login-image">
          <div className="geometric-shapes">
            <div className="geometric-shape shape-1"></div>
            <div className="geometric-shape shape-2"></div>
            <div className="geometric-shape shape-3"></div>
            <div className="geometric-shape shape-4"></div>
          </div>
          <div className="login-info">
            <h2>Sign Into Soulmegle</h2>
            <p>Continue your journey to find meaningful connections and discover your perfect match through our intelligent platform.</p>
            <ul className="login-features">
              <li>
                <div className="feature-icon">
                  <svg fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd"/>
                  </svg>
                </div>
                <span>Access your personalized profile</span>
              </li>
              <li>
                <div className="feature-icon">
                  <svg fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd"/>
                  </svg>
                </div>
                <span>Resume previous conversations</span>
              </li>
              <li>
                <div className="feature-icon">
                  <svg fill="currentColor" viewBox="0 0 20 20">
                    <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                  </svg>
                </div>
                <span>Enjoy enhanced matching algorithms</span>
              </li>
              <li>
                <div className="feature-icon">
                  <svg fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM4.332 8.027a6.012 6.012 0 011.912-2.706C6.512 5.73 6.974 6 7.5 6A1.5 1.5 0 019 7.5V8a2 2 0 004 0 2 2 0 011.523-1.943A5.977 5.977 0 0116 10c0 .34-.028.675-.083 1H15a2 2 0 00-2 2v2.197A5.973 5.973 0 0110 16v-2a2 2 0 00-2-2 2 2 0 01-2-2 2 2 0 00-1.668-1.973z" clipRule="evenodd"/>
                  </svg>
                </div>
                <span>Connect with global community</span>
              </li>
            </ul>
          </div>
        </div>

        {showAlert && (
          <AlertBox
            message={alertMessage}
            onClose={closeAlert}
            success="failure"
          />
        )}
      </div>
    </>
  );
}

export default LoginPage;